/**
 * Admin admissions API. Mirrors the /admin/events/:id/admissions/* routes.
 *
 * Every call is scoped to an event, because that is the operational context (§55). There is
 * no standalone admissions console and nothing here lists across events.
 */

import { api } from './apiClient';
import type { AdmissionMode } from './admissions';

export interface AdmissionSettings {
  eventId: string;
  mode: AdmissionMode;
  preset: string | null;
  waitlistEnabled: boolean;
  partySizeMin: number;
  partySizeMax: number;
  admissionCapacity: number | null;
  opensAt: string | null;
  closesAt: string | null;
  physicalCapacityOverrideBy: string | null;
  physicalCapacityOverrideAt: string | null;
  physicalCapacityOverrideReason: string | null;
  /** True when this event has never been configured and runs on the platform default. */
  isDefault?: boolean;
}

export interface AdmissionActivity {
  soldTickets: number;
  admissions: number;
  openRequests: number;
  activeWaitlist: number;
}

export interface AdmissionSummary {
  eventId: string;
  settings: AdmissionSettings;
  activity: AdmissionActivity;
  admitted: number;
  capacity: number | null;
  remaining: number | null;
  /** The room, when the event has one. A safety ceiling, not the inventory. */
  space: { id: string; name: string | null; capacity: number | null } | null;
}

export interface AdmissionRequestRow {
  id: string;
  eventId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  partySize: number;
  status: 'pending' | 'approved' | 'declined' | 'cancelled' | 'expired';
  source: string;
  orderId: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedByEmail: string | null;
  /** Operations only. Never rendered to the attendee. */
  internalNotes: string | null;
  declineMessage: string | null;
}

export interface WaitlistRow {
  id: string;
  eventId: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  partySize: number;
  status: 'waiting' | 'offered' | 'accepted' | 'declined' | 'expired' | 'removed';
  /** The operator's queue order. Deterministic, and deliberately never shown publicly. */
  sequence: number;
  orderId: string | null;
  joinedAt: string;
  offeredAt: string | null;
  offerExpiresAt: string | null;
  internalNotes: string | null;
}

export interface ConfirmedAdmission {
  orderId: string;
  email: string;
  name: string;
  partySize: number;
  checkedIn: number;
  totalCents: number;
  /** Recorded at fulfilment, never inferred from a zero total. */
  source: 'ticket_purchase' | 'rsvp' | 'request_approval' | 'waitlist' | 'invitation';
  confirmedAt: string;
}

export interface Invitation {
  id: string;
  eventId: string;
  email: string | null;
  maxUses: number;
  usedCount: number;
  partySizeMax: number;
  expiresAt: string | null;
  status: 'active' | 'revoked';
  label: string | null;
  createdAt: string;
}

export const getAdmissionSummary = (eventId: string) =>
  api.get<AdmissionSummary>(`/admin/events/${eventId}/admissions`);

export const listAdmissionModes = () =>
  api.get<{ modes: AdmissionMode[]; sources: string[] }>('/admin/admissions/modes');

export const updateAdmissionSettings = (eventId: string, patch: Partial<AdmissionSettings> & {
  authorizeOverPhysicalCapacity?: boolean; overrideReason?: string | null;
}) => api.put<AdmissionSettings>(`/admin/events/${eventId}/admissions`, patch);

export const applyStudioPreset = (eventId: string, body: { admissionCapacity?: number } = {}) =>
  api.post<AdmissionSettings>(`/admin/events/${eventId}/admissions/studio-preset`, body);

export const listAdmissionRequests = (eventId: string, status?: string | null) =>
  api.get<AdmissionRequestRow[]>(
    `/admin/events/${eventId}/admissions/requests${status ? `?status=${status}` : ''}`);

export const approveRequest = (requestId: string, notes?: string | null) =>
  api.post<{ request: AdmissionRequestRow; orderId: string; ticketCount: number }>(
    `/admin/admissions/requests/${requestId}/approve`, { notes });

export const declineRequest = (requestId: string, body: {
  notes?: string | null; message?: string | null;
}) => api.post<AdmissionRequestRow>(`/admin/admissions/requests/${requestId}/decline`, body);

export const listAdmissionWaitlist = (eventId: string, status?: string | null) =>
  api.get<WaitlistRow[]>(
    `/admin/events/${eventId}/admissions/waitlist${status ? `?status=${status}` : ''}`);

export const offerWaitlistPlace = (entryId: string, expiresInHours = 48) =>
  api.post<WaitlistRow>(`/admin/admissions/waitlist/${entryId}/offer`, { expiresInHours });

export const promoteWaitlistPlace = (entryId: string) =>
  api.post<{ entry: WaitlistRow; orderId: string; ticketCount: number }>(
    `/admin/admissions/waitlist/${entryId}/promote`, {});

export const removeWaitlistPlace = (entryId: string, notes?: string | null) =>
  api.post<WaitlistRow>(`/admin/admissions/waitlist/${entryId}/remove`, { notes });

export const listConfirmedAdmissions = (eventId: string) =>
  api.get<ConfirmedAdmission[]>(`/admin/events/${eventId}/admissions/confirmed`);

export const cancelConfirmedAdmission = (orderId: string, reason?: string | null) =>
  api.post<{ cancelled: number }>(`/admin/admissions/orders/${orderId}/cancel`, { reason });

export const listInvitations = (eventId: string) =>
  api.get<Invitation[]>(`/admin/events/${eventId}/admissions/invitations`);

/** The raw token comes back ONCE and is unrecoverable: the screen shows it now or never. */
export const createInvitation = (eventId: string, body: {
  email?: string | null; maxUses?: number; partySizeMax?: number;
  expiresAt?: string | null; label?: string | null;
}) => api.post<{ invitation: Invitation; token: string }>(
  `/admin/events/${eventId}/admissions/invitations`, body);

export const revokeInvitation = (invitationId: string) =>
  api.post<{ revoked: true }>(`/admin/admissions/invitations/${invitationId}/revoke`, {});

export const MODE_LABEL: Record<AdmissionMode, string> = {
  ticketed: 'Ticketed',
  rsvp: 'RSVP',
  request: 'Request to attend',
  invite_only: 'Invite only',
  donation: 'Donation',
};

export const SOURCE_LABEL: Record<string, string> = {
  ticket_purchase: 'Purchase',
  rsvp: 'RSVP',
  request_approval: 'Request',
  waitlist: 'Waitlist',
  invitation: 'Invitation',
};
